<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent" v-if="!isModal"></column-header>
    <div>
      <div class="row" v-if="!isModal">
				<div class="col-md-12">
          <h3 v-if="deliverymethod.name">{{ deliverymethod.name }}</h3>
          <h3 v-else>{{ _('Delivery method') }}</h3>
        </div>
      </div>
      <div class="modal-header" v-else>
        <h3 v-if="deliverymethod.name">{{ deliverymethod.name }}</h3>
        <h3 v-else>{{ _('Delivery method') }}</h3>
      </div>
      <div :class="(isModal ? 'modal-body' : '')">
        <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
        <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
        <div class="tab-content">
          <transition-group name="fade">
            <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
              <div class="row">
                <div class="col-sm-6 leftcol">
                  <h3>{{ _('General') }}</h3>
                  <div class="form-group languageversioned">
                    <label for="name">{{ _('Name') }}</label>
                    <input type="text" class="form-control" v-model="deliverymethod.name" :placeholder="_('Enter the delivery method\'s name')" />
                  </div>
                  <template v-if="!parseInt(deliverymethod.internal) && !parseInt(deliverymethod.shipit_pricing)">
                    <div class="form-group">
                      <label for="price">{{ _('Price inc. vat') }}</label>
                      <div class="input-group">
                        <input type="text" class="form-control" v-model="deliverymethod.price" :placeholder="_('Enter the delivery method\'s price')" />
                        <div class="input-group-addon">{{ settings.currency }}</div>
                      </div>
                    </div>
                    <div class="form-group">
                      <label for="cutoffprice">{{ _('Free for orders exceeding') }}</label>
                      <div class="input-group">
                        <input type="text" class="form-control" v-model="deliverymethod.cutoffprice" :placeholder="_('Enter the minimum total price of orders that qualify for free shipping')" />
                        <div class="input-group-addon">{{ settings.currency }}</div>
                      </div>
                    </div>
                  </template>
                  <div class="form-group">
                    <label>
                      <input type="checkbox" v-model="deliverymethod.internal" true-value="1" false-value="0" /> {{ _('Internal delivery method') }}
                    </label>
                  </div>
                  <div class="form-group" v-if="parseInt(deliverymethod.internal)">
                    <label>
                      <input type="checkbox" v-model="deliverymethod.default" true-value="1" false-value="0" /> {{ _('Default delivery method') }}
                    </label>
                  </div>
                  <div class="form-group" v-if="isFeatureEnabled('manifestprinter')">
                    <label>
                      <input type="checkbox" v-model="deliverymethod.autoprint" true-value="1" false-value="0" /> {{ _('Print delivery manifest automatically') }}
                    </label>
                  </div>
                </div>
                <div class="col-sm-6 rightcol">
                  <div class="order-tags-title">
                    <h3>{{ _('Tags') }}</h3>
                  </div>
                  <div class="order-tags-selector">
                    <div class="form-group">
                      <label>{{ _('Tags to add to deliveries created by this delivery method') }}</label>
                      <div class="input-group">
                        <model-list-select :list="allSelectableTags" v-model="tagToAdd" v-on:searchchange="tagSearchChanged" option-value="id" option-text="title" :placeholder="_('Type to search or add tags')"></model-list-select>
                        <span class="input-group-btn">
                          <button type="button" class="btn btn-info tagsmanagement" v-on:click="manageTagsDialog('delivery')"><i class="fa fa-fw fa-tags"></i> {{ _('Manage tags') }}</button>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="order-tags-selected">
                    <div class="tags-list">
                      <span v-for="tag in tags" class="btn btn-info" v-on:click="removeTag(tag)"><i class="fa fa-fw fa-close"></i> {{ getTagTitle(tag) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-bind="tabPanel('shipping')" class="tab-pane active" id="shipping" v-if="isTabActive('shipping')" key="shipping">
              <div class="row">
                <div class="col-sm-6 leftcol">
                  <h3>{{ _('Shipment dimensions') }}</h3>
                  <div class="form-group">
                    <label for="maxHeight">{{ _('Maximum height') }}</label>
                    <div class="input-group">
                      <input type="text" class="form-control" v-model="deliverymethod.maxheight" :placeholder="_('The maximum height supported by this delivery method')" />
                      <div class="input-group-addon">{{ _('cm') }}</div>
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="maxWidth">{{ _('Maximum width') }}</label>
                    <div class="input-group">
                      <input type="text" class="form-control" v-model="deliverymethod.maxwidth" :placeholder="_('The maximum width supported by this delivery method')" />
                      <div class="input-group-addon">{{ _('cm') }}</div>
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="maxDepth">{{ _('Maximum depth') }}</label>
                    <div class="input-group">
                      <input type="text" class="form-control" v-model="deliverymethod.maxdepth" :placeholder="_('The maximum depth supported by this delivery method')" />
                      <div class="input-group-addon">{{ _('cm') }}</div>
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="maxWeight">{{ _('Maximum weight') }}</label>
                    <div class="input-group">
                      <input type="text" class="form-control" v-model="deliverymethod.maxweight" :placeholder="_('The maximum weight supported by this delivery method')" />
                      <div class="input-group-addon">{{ _('kg') }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-sm-6 rightcol">
                  <template v-if="isShipitEnabled">
                    <h3>{{ _('Shipit') }}</h3>
                    <div class="form-group">
                      <label for="shipitId">{{ _('Delivery method') }}</label>
                      <select id="shipitId" v-model="deliverymethod.shipit_id" class="form-control">
                        <option :value="0">{{ _('Not selected') }}</option>
                        <option v-for="shipitMethod in shipitMethods" :value="shipitMethod.id"><template v-if="shipitMethod.carrier">{{ shipitMethod.carrier}} / </template>{{ shipitMethod.name }}</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label>
                        <input type="checkbox" v-model="deliverymethod.shipit_pricing" true-value="1" false-value="0" /> {{ _('Use Shipit pricing') }}
                      </label>
                    </div>
                    <div class="form-group">
                      <label>
                        <input type="checkbox" v-model="deliverymethod.shipit_returnfreightdoc" true-value="1" false-value="0" /> {{ _('Include a return freight document') }}
                      </label>
                    </div>
                    <div class="form-group" v-if="parseInt(deliverymethod.shipit_pricing)">
                      <label for="shipitMargin">{{ _('Margin') }}</label>
                      <div class="input-group">
                        <input type="text" class="form-control" v-model="deliverymethod.shipit_margin" :placeholder="_('The percentage to add on top of the Shipit price')" />
                        <div class="input-group-addon">%</div>
                      </div>
                    </div>
                    <div class="form-group">
                      <label for="shipitSenderAddress">{{ _('Shipit sender address') }}</label>
                      <p v-if="shipitAddress">{{ shipitAddress.company }}, {{ shipitAddress.address }}, {{ shipitAddress.zip }} {{ shipitAddress.city }}</p>
                      <p v-else>{{ _('Use default address') }}</p>
                      <button class="btn btn-info" v-on:click="editShipitAddressDialog"><i class="fa fa-fw fa-pencil"></i> {{ _('Edit address') }}</button>
                      <button v-if="shipitAddress" class="btn btn-default" v-on:click="removeShipitAddress"><i class="fa fa-fw fa-trash"></i> {{ _('Use default address') }}</button>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div v-bind="tabPanel('restrictions')" class="tab-pane active" id="restrictions" v-if="isTabActive('restrictions')" key="restrictions">
              <div class="row">
                <div class="col-sm-6 leftcol">
                  <h3>{{ _('Allowed for products in categories') }}</h3>
                  <div class="form-group" style="max-height: 400px; overflow-y: auto">
                    <draggable v-if="allowedCategories && allowedCategories.length" v-model="allowedCategories" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-groups list-group' }}">
                      <li :class="'list-group-item' + (selectedAllowedCategory === categoryId ? ' list-group-item-info' : '')" v-for="categoryId in allowedCategories" v-on:click="allowedCategoryClicked(categoryId)">{{ getCategoryName(categoryId) }}</li>
                    </draggable>
                    <ul class="list-group" v-else>
                      <li class="list-group-item"><i>{{ _('No categories selected') }}</i></li>
                    </ul>
                  </div>
                  <div class="form-group">
                    <button type="button" class="btn btn-success" v-on:click="addAllowedCategoryDialog"><span class="fa fa-fw fa-plus"></span> {{ _('Add category') }}</button>
                    <button type="button" class="btn btn-danger" v-if="selectedAllowedCategory" v-on:click="removeAllowedCategory"><span class="fa fa-fw fa-trash"></span> {{ _('Delete') }}</button>
                  </div>
                  <h3>{{ _('Allowed for products') }}</h3>
                  <div class="form-group" style="max-height: 400px; overflow-y: auto">
                    <draggable v-if="allowedProducts && allowedProducts.length" v-model="allowedProducts" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-groups list-group' }}">
                      <li :class="'list-group-item' + (selectedAllowedProduct === productId ? ' list-group-item-info' : '')" v-for="productId in allowedProducts" v-on:click="allowedProductClicked(productId)">{{ getAllowedProductName(productId) }}</li>
                    </draggable>
                    <ul class="list-group" v-else>
                      <li class="list-group-item"><i>{{ _('No products selected') }}</i></li>
                    </ul>
                  </div>
                  <div class="form-group">
                    <button type="button" class="btn btn-success" v-on:click="addAllowedProductDialog"><span class="fa fa-fw fa-plus"></span> {{ _('Add product') }}</button>
                    <button type="button" class="btn btn-danger" v-if="selectedAllowedProduct" v-on:click="removeAllowedProduct"><span class="fa fa-fw fa-trash"></span> {{ _('Delete') }}</button>
                  </div>
                  <h3>{{ _('Excluded products') }}</h3>
                  <div class="form-group" style="max-height: 400px; overflow-y: auto">
                    <draggable v-if="excludedProducts && excludedProducts.length" v-model="excludedProducts" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-groups list-group' }}">
                      <li :class="'list-group-item' + (selectedExcludedProduct === productId ? ' list-group-item-info' : '')" v-for="productId in excludedProducts" v-on:click="excludedProductClicked(productId)">{{ getExcludedProductName(productId) }}</li>
                    </draggable>
                    <ul class="list-group" v-else>
                      <li class="list-group-item"><i>{{ _('No products selected') }}</i></li>
                    </ul>
                  </div>
                  <div class="form-group">
                    <button type="button" class="btn btn-success" v-on:click="addExcludedProductDialog"><span class="fa fa-fw fa-plus"></span> {{ _('Add product') }}</button>
                    <button type="button" class="btn btn-danger" v-if="selectedExcludedProduct" v-on:click="removeExcludedProduct"><span class="fa fa-fw fa-trash"></span> {{ _('Delete') }}</button>
                  </div>
                </div>
                <div class="col-sm-6 rightcol">
                  <h3>{{ _('Numerical limits') }}</h3>
                  <div class="form-group">
                    <label>
                      <input type="checkbox" v-model="deliverymethod.singleproduct" true-value="1" false-value="0" /> {{ _('This delivery method can only ship one product') }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div v-bind="tabPanel('timing')" class="tab-pane active" id="timing" v-if="isTabActive('timing')" key="timing">
              <div class="row">
                <div class="col-sm-6 leftcol">
                  <h3>{{ _('Delivery time') }}</h3>
                  <div class="panel-group flex-panel" role="tablist">
                    <div :class="deliveryPanelClass('immediate')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('immediate')">
                        <h4 class="panel-title">
                          <input type="checkbox" v-model="selectedTimeType" value="immediate" />
                          <span class="title-text">{{ _('Immediate') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('immediate')" role="tabpanel">
                        <div class="panel-body">
                          <div class="form-group">
                            <p>{{ _('Delivery immediately after the order has been created.') }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('fixed')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('fixed')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="fixed" />
                          <span class="title-text">{{ _('Set time') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('fixed')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery at a set date and time.') }}</p>
                          <DatePicker v-model="deliveryDate" :shortLocale="shortLocale" :staticInput="true" :time-picker="true" :time-picker-inline="true"></DatePicker>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterorder')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterorder')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterorder" />
                          <span class="title-text">{{ _('After the order has been created') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterorder')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the order has been created.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterorder"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('weekdaysafterorder')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('weekdaysafterorder')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="weekdaysafterorder" />
                          <span class="title-text">{{ _('At a specific weekday after the order has been created') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('weekdaysafterorder')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery on the next allowed weekday a number of days after the order has been created.') }}</p>
                          <div class="form-group">
                            <label>{{ _('Allowed weekdays') }}</label>
                            <div class="input-group">
                              <div class="btn-group daybuttons" id="dayRangeButtons">
                                <label :class="'btn btn-sm ' + (weekdays.includes('1') ? 'btn-success' : 'btn-default')" data-value="1">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="1" /> {{ _('mo') }}
                                </label> 
                                <label :class="'btn btn-sm ' + (weekdays.includes('2') ? 'btn-success' : 'btn-default')" data-value="2">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="2" /> {{ _('tu') }}
                                </label> 
                                <label :class="'btn btn-sm ' + (weekdays.includes('3') ? 'btn-success' : 'btn-default')" data-value="3">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="3" /> {{ _('we') }}
                                </label> 
                                <label :class="'btn btn-sm ' + (weekdays.includes('4') ? 'btn-success' : 'btn-default')" data-value="4">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="4" /> {{ _('th') }}
                                </label> 
                                <label :class="'btn btn-sm ' + (weekdays.includes('5') ? 'btn-success' : 'btn-default')" data-value="5">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="5" /> {{ _('fr') }}
                                </label> 
                                <label :class="'btn btn-sm ' + (weekdays.includes('6') ? 'btn-success' : 'btn-default')" data-value="6">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="6" /> {{ _('sa') }}
                                </label> 
                                <label :class="'btn btn-sm ' + (weekdays.includes('7') ? 'btn-success' : 'btn-default')" data-value="7">
                                  <input type="checkbox" autocomplete="off" v-model="weekdays" value="7" /> {{ _('su') }}
                                </label>
                              </div>
                            </div>
                          </div>
                          <div class="form-group">
                            <label>{{ _('Delay') }}</label>
                            <div class="input-group">
                              <input class="form-control" v-model="deliverymethod.weekdaysafterorder" type="text">
                              <div class="input-group-addon">{{ _('d') }}</div>
                            </div>
                          </div>
                          <label>{{ _('Delivery time') }}</label>
                          <div class="form-group">
                            <TimePicker :shortLocale="shortLocale" v-model="weekdaysTime" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('beforereservationstart')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('beforereservationstart')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="beforereservationstart" />
                          <span class="title-text">{{ _('Before the reservation starts') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('beforereservationstart')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes before the reservation of the product in this delivery starts.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.beforereservationstart"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterreservationstart')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterreservationstart')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterreservationstart" />
                          <span class="title-text">{{ _('After the reservation starts') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterreservationstart')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the reservation of the product in this delivery starts.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterreservationstart"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('beforereservationend')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('beforereservationend')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="beforereservationend" />
                          <span class="title-text">{{ _('Before the reservation ends') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('beforereservationend')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes before the reservation of the product in this delivery ends.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.beforereservationend"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterreservationend')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterreservationend')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterreservationend" />
                          <span class="title-text">{{ _('After the reservation ends') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterreservationend')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the reservation of the product in this delivery ends.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterreservationend"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('beforeparentreservationstart')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('beforeparentreservationstart')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="beforeparentreservationstart" />
                          <span class="title-text">{{ _('Before the parent reservation starts') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('beforeparentreservationstart')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes before the reservation of the main product of the product option in this delivery starts.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.beforeparentreservationstart"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterparentreservationstart')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterparentreservationstart')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterparentreservationstart" />
                          <span class="title-text">{{ _('After the parent reservation starts') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterparentreservationstart')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the reservation of the main product of the product option in this delivery starts.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterparentreservationstart"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('beforeparentreservationend')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('beforeparentreservationend')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="beforeparentreservationend" />
                          <span class="title-text">{{ _('Before the parent reservation ends') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('beforeparentreservationend')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes before the reservation of the main product of the product option in this delivery ends.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.beforeparentreservationend"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterparentreservationend')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterparentreservationend')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterparentreservationend" />
                          <span class="title-text">{{ _('After the parent reservation ends') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterparentreservationend')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the reservation of the main product of the product option in this delivery ends.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterparentreservationend"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('beforefirstreservation')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('beforefirstreservation')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="beforefirstreservation" />
                          <span class="title-text">{{ _('Before the first reservation in the order starts') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('beforefirstreservation')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes before the first reservation in the order starts.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.beforefirstreservation"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterfirstreservation')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterfirstreservation')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterfirstreservation" />
                          <span class="title-text">{{ _('After the first reservation in the order has ended') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterfirstreservation')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the first reservation in the order has ended.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterfirstreservation"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('beforelastreservation')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('beforelastreservation')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="beforelastreservation" />
                          <span class="title-text">{{ _('Before the last reservation in the order starts') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('beforelastreservation')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes before the last reservation in the order starts.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.beforelastreservation"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                    <div :class="deliveryPanelClass('afterlastreservation')">
                      <div class="panel-heading" role="tab" v-on:click.stop="selectTimeType('afterlastreservation')">
                        <h4 class="panel-title">
                          <input type="checkbox" class="pull-right" v-model="selectedTimeType" value="afterlastreservation" />
                          <span class="title-text">{{ _('After the last reservation in the order has ended') }}</span>
                        </h4>
                      </div>
                      <div :class="deliveryPanelCollapseClass('afterlastreservation')" role="tabpanel">
                        <div class="panel-body">
                          <p>{{ _('Delivery a number of days, hours or minutes after the last reservation in the order has ended.') }}</p>
                          <day-hour-minute-selector v-model="deliverymethod.afterlastreservation"></day-hour-minute-selector>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-bind="tabPanel('locking')" class="tab-pane active" id="locking" v-if="isTabActive('locking')" key="locking">
              <div class="row">
                <div class="col-sm-6 leftcol">
                  <h3>{{ _('Locking') }}</h3>
                  <div class="form-group">
                    <label v-if="deliverymethod.igloohome_lockcode">
                    {{ _('Linked to Igloohome lock') }}: {{ deliverymethod.igloohome_lockname }}
                    </label>
                    <label v-else-if="deliverymethod.ratna_lockcode">
                    {{ _('Linked to Ratna lock') }}: {{ deliverymethod.ratna_lockname }} ({{ deliverymethod.ratna_lockcode }})
                    </label>
                    <label v-else>
                    {{ _('Select a lock to link this delivery method to.') }}
                    </label>
                  </div>
                  <template v-if="settings.igloohome_apikey && !deliverymethod.ratna_lockcode">
                    <button class="selectigloo btn btn-success" id="selectIglooButton" type="button" v-if="!deliverymethod.igloohome_lockcode" v-on:click="selectIglooLock"><i class="fa fa-fw fa-lock"></i> {{ _('Select Igloohome lock') }}</button>
                    <button class="detachigloo btn btn-danger" id="detachIglooButton" type="button" v-else v-on:click="detachIglooLock"><i class="fa fa-fw fa-lock"></i> {{ _('Detach Igloohome lock') }}</button>
                  </template>
                  <template v-if="settings.ratna_username && !deliverymethod.igloohome_lockcode">
                    <button class="selectratna btn btn-success" id="selectRatnaButton" type="button" v-if="!deliverymethod.ratna_lockcode" v-on:click="selectRatnaApartment"><i class="fa fa-fw fa-lock"></i> {{ _('Select Ratna apartment') }}</button>
                    <button class="detachratna btn btn-danger" id="detachRatnaButton" type="button" v-else v-on:click="detachRatnaApartment"><i class="fa fa-fw fa-lock"></i> {{ _('Detach Ratna apartment') }}</button>
                  </template>
                  <div class="form-group" v-if="deliverymethod.igloohome_lockcode || deliverymethod.ratna_lockcode" style="margin-top: 30px">
                    <label>{{ _('Duration') }}</label>
                    <div class="input-group">
                      <input class="form-control" type="number" data-android-inputmode="decimal" v-model="deliverymethod.lockduration" />
                      <div class="input-group-addon">{{ _('h') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-bind="tabPanel('messages')" class="tab-pane active" id="messages" v-if="isTabActive('messages')" key="messages">
              <div class="row">
                <div class="col-xs-12 languageversioned">
                  <h3>{{ _('Scheduled messages') }}</h3>
                </div>
              </div>
              <div class="row">
                <div class="col-sm-6 leftcol">
                  <table class="table table-striped" v-if="notifications.length">
                    <thead>
                      <tr>
                        <th>{{ _('Name') }}</th>
                        <th colspan="2">{{ _('Type') }}</th>
                      </tr>
                    </thead>
                    <tbody class="messagerows" id="notifications">
                      <tr v-for="notification in notifications">
                        <td class="notification" v-on:click="editNotification(notification)">{{ makeNotificationDesc(notification) }}</td>
                        <td v-on:click="editNotification(notification)">{{ makeNotificationType(notification) }} </td>
                        <td style="width: 20px"><a class="deleteNotification pull-right" href="javascript:;" v-on:click="deleteNotification(notification)"><i class="fa fa-fw fa-close"></i></a></td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-else>
                    <p>{{ _('No notifications have been defined.') }}</p>
                  </div>
                  <button type="button" class="addmessage btn btn-success" v-on:click="addNotification()"><i class="fa fa-fw fa-plus"></i> {{ _('Add message') }}</button>
                </div>
                <div class="col-sm-6 rightcol">
                  <div class="row">
                    <div class="col-xs-12">
                      <p></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition-group>
        </div>
      </div>
    </div>
    <nav :class="(isModal ? 'modal-footer' : 'save-bar')">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-on:click="remove" type="button" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import netvisor from '~/lib/netvisor'
import notifications from '~/lib/notifications'
import settingshelpers from '~/lib/settingshelpers'
import { AddressDetails, DatetimePicker, DayHourMinuteSelector, ModalDeliveryNotificationDetails as DeliveryNotificationDetails, DraggableMultiSelector, CategorySelector, IglooLockSelector, ProductSelector, TagsList } from '#components'
import { ModelListSelect } from 'vue-search-select'

export default {
  data () {
    return {
      shipitAddress: null,
      deliverymethod: {},
      shipitMethods: [],
      allTags: [],
      allCategories: [],
      allProducts: [],
      tagToAdd: {},
      tagSearchText: '',
      selectedAllowedCategory: null,
      selectedAllowedProduct: null,
      selectedExcludedProduct: null,
      notifications: []
    }
  },
  mixins: [components, netvisor, notifications, settingshelpers],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'shipping', label: this._('Shipping'), hidden: !!parseInt(this.deliverymethod.internal) },
        { id: 'restrictions', label: this._('Restrictions') },
        { id: 'timing', label: this._('Timing') },
        { id: 'locking', label: this._('Locking'), hidden: !this.isLockingVisible },
        { id: 'messages', label: this._('Messages') }
      ])
    },
    weekdays: {
      get() {
        return this.deliverymethod && this.deliverymethod.weekdays && this.deliverymethod.weekdays.split ? this.deliverymethod.weekdays.split(',') : []
      },
      set(newValue) {
        this.deliverymethod.weekdays = newValue.sort().join(',')
      }
    },
    isLockingVisible() {
      return this.settings && (this.settings.igloohome_apikey || this.settings.ratna_username)
    },
    ratnaApartments: {
      get () {
        return this.settings && this.settings.ratna_apartments ? JSON.parse(this.settings.ratna_apartments) : []
      },
      set () {}
    },
    deliveryDate: {
      get () {
        // fixedtime is stored as Unix seconds. Older saves stored only the year (e.g. 2027); show those as empty so the date gets picked again
        if (!this.deliverymethod || !(Number(this.deliverymethod.fixedtime) > 100000)) {
          return null
        }
        return DateTime.fromSeconds(Number(this.deliverymethod.fixedtime)).toJSDate()
      },
      set (newValue) {
        if (newValue) {
          this.deliverymethod.fixedtime = DateTime.fromJSDate(newValue).toUnixInteger()
        } else {
          this.deliverymethod.fixedtime = null
        }
      }
    },
    weekdaysTime: {
      get () {
        if (!this.deliverymethod || !this.deliverymethod.weekdaystime) {
          return null
        }
        return {
          hours: this.deliverymethod.weekdaystime.split(':')[0],
          minutes: this.deliverymethod.weekdaystime.split(':')[1],
          seconds: 0
        }
      },
      set (newValue) {
        if (newValue) {
          this.deliverymethod.weekdaystime = `${newValue.hours}:${newValue.minutes}`
        } else {
          this.deliverymethod.weekdaystime = null
        }
      }
    },
    selectedTimeType: {
      get () {
        return this.deliverymethod && this.deliverymethod.timetype ? [this.deliverymethod.timetype] : []
      },
      set (newValue) {
        if (newValue && newValue.length) {
          newValue = newValue.filter(v => !this.selectedTimeType.includes(v))
          this.deliverymethod.timetype = newValue[0]
        } else {
          this.deliverymethod.timetype = null
        }
      }
    },
    tags () {
      return this.deliverymethod && this.deliverymethod.sharedTag ? this.deliverymethod.sharedTag.filter(t => t.title) : []
    },
    allSelectableTags () {
      const ids = this.tags.map(t => t.id)
      if (this.tagSearchText) {
        return [...this.allTags.filter(t => !ids.includes(t.id)), { id: 0, title: this._('Add') + ' ' + this.tagSearchText }]
      }
      return this.allTags.filter(t => !ids.includes(t.id))
    },
    allowedCategories: {
      get () {
        return this.deliverymethod && this.deliverymethod.sharedCategory ? this.deliverymethod.sharedCategory.map(c => c.id) : []
      },
      set (newValue) {
        if (!newValue || !newValue.length) {
          this.deliverymethod.sharedCategory = []
          return
        }
        let categories = []
        newValue.forEach(id => {
          categories.push(this.allCategories.find(c => parseInt(c.id, 10) === parseInt(id, 10)))
        })
        this.deliverymethod.sharedCategory = categories
      }
    },
    allowedProducts: {
      get () {
        return this.deliverymethod && this.deliverymethod.sharedProduct ? this.deliverymethod.sharedProduct.map(p => p.id) : []
      },
      set (newValue) {
        if (!newValue || !newValue.length) {
          this.deliverymethod.sharedProduct = []
          return
        }
        let products = []
        newValue.forEach(id => {
          const prod = this.allProducts.find(p => parseInt(p.id, 10) === parseInt(id, 10))
          if (prod) {
            products.push(prod)
          }
        })
        this.deliverymethod.sharedProduct = products
      }

    },
    excludedProducts: {
      get () {
        return this.deliverymethod && this.deliverymethod.excludedProduct ? this.deliverymethod.excludedProduct.map(p => p.id) : []
      },
      set (newValue) {
        if (!newValue || !newValue.length) {
          this.deliverymethod.excludedProduct = []
          return
        }
        let products = []
        newValue.forEach(id => {
          const prod = this.allProducts.find(p => parseInt(p.id, 10) === parseInt(id, 10))
          if (prod) {
            products.push(prod)
          }
        })
        this.deliverymethod.excludedProduct = products
      }
    },
  },
  methods: {
    manageTagsDialog (category) {
      this.showModal('tagslist', TagsList, {
        props: {
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              category: category,
              modal: true
            }
          },
          className: 'modal-content'
        },
        events: {},
        handlers: {},
        width: 1000
      })
    },
    async editNotification(notification, allowEmbedded) {
      this.showModal('notificationdetails', DeliveryNotificationDetails, {
        props: {
          selectedNotification: notification,
          allTags: this.allTags,
          settings: this.settings
        },
        events: {
          save: this.saveNotification,
          remove: this.deleteNotification
        },
        handlers: {},
        width: 800
      })
    },
    editShipitAddressDialog () {
      this.showModal('shipitaddressdetails', AddressDetails, {
        props: {
          value: this.shipitAddress
        },
        events: {
          save: this.setShipitAddress
        },
        handlers: {},
        width: 1000
      })
    },
    setShipitAddress (data) {
      this.shipitAddress = data
    },
    removeShipitAddress () {
      this.shipitAddress = null
    },
    selectTimeType(timetype) {
      if (!this.selectedTimeType.includes(timetype)) {
        this.selectedTimeType = [timetype]
      } else {
        this.selectedTimeType = []
      }
    },
    deliveryPanelClass(panel) {
      return 'panel panel-' + (this.deliverymethod && this.deliverymethod.timetype === panel ? 'info' : 'default')
    },
    deliveryPanelCollapseClass(panel) {
      return 'panel-collapse ' + (this.deliverymethod && this.deliverymethod.timetype === panel ? '' : 'collapse')
    },
    getTagTitle(tag) {
      if (!tag || !tag.title) {
        return ''
      }
      const parts = tag.title.split(':::')
      return parts.pop()
    },
    removeTag(tag) {
      this.deliverymethod.sharedTag = this.deliverymethod.sharedTag.filter(t => t.title !== tag.title)
    },
    tagSearchChanged (text) {
      this.tagSearchText = text
    },
    allowedCategoryClicked (id) {
      if (this.selectedAllowedCategory === id) {
        this.selectedAllowedCategory = null
      } else {
        this.selectedAllowedCategory = id
      }
    },
    addAllowedCategoryDialog () {
      this.showModal('categoryselector', CategorySelector, {
        props: {
          selectedCategories: this.allowedCategories.map(c => { return { id: c } }),
          allowSelectAll: true
        },
        events: {
          selectCategory: this.addAllowedCategory
        },
        handlers: {},
        width: 800
      })
    },
    addAllowedCategory (category) {
      if (Array.isArray(category)) {
        category.forEach(cat => {
          this.deliverymethod.sharedCategory.push(cat)
        })
      } else {
        this.deliverymethod.sharedCategory.push(category)
      }
    },
    removeAllowedCategory () {
      if (!this.selectedAllowedCategory) {
        return
      }
      this.allowedCategories = this.allowedCategories.filter(c => parseInt(c, 10) !== parseInt(this.selectedAllowedCategory, 10))
      this.selectedAllowedCategory = null
    },
    allowedProductClicked (id) {
      if (this.selectedAllowedProduct === id) {
        this.selectedAllowedProduct = null
      } else {
        this.selectedAllowedProduct = id
      }
    },
    addAllowedProductDialog () {
      this.showModal('productselector', ProductSelector, {
        props: {
          selectedProducts: this.allowedProducts.map(c => { return { id: c } })
        },
        events: {
          selectProduct: this.addAllowedProduct
        },
        handlers: {},
        width: 800
      })
    },
    addAllowedProduct (product) {
      this.deliverymethod.sharedProduct.push(product)
    },
    removeAllowedProduct () {
      if (!this.selectedAllowedProduct) {
        return
      }
      this.allowedProducts = this.allowedProducts.filter(c => parseInt(c, 10) !== parseInt(this.selectedAllowedProduct, 10))
      this.selectedAllowedProduct = null
    },
    excludedProductClicked (id) {
      if (this.selectedExcludedProduct === id) {
        this.selectedExcludedProduct = null
      } else {
        this.selectedExcludedProduct = id
      }
    },
    addExcludedProductDialog () {
      this.showModal('productselector', ProductSelector, {
        props: {
          selectedProducts: this.excludedProducts.map(c => { return { id: c } })
        },
        events: {
          selectProduct: this.addExcludedProduct
        },
        handlers: {},
        width: 800
      })
    },
    addExcludedProduct (product) {
      this.deliverymethod.excludedProduct.push(product)
    },
    removeExcludedProduct () {
      if (!this.selectedExcludedProduct) {
        return
      }
      this.excludedProducts = this.excludedProducts.filter(c => parseInt(c, 10) !== parseInt(this.selectedExcludedProduct, 10))
      this.selectedExcludedProduct = null
    },
    getCategoryName (id) {
      const category = this.allCategories.find(c => parseInt(c.id, 10) === parseInt(id, 10))
      if (category) {
        return category.name
      }
      return ''
    },
    getAllowedProductName (id) {
      const product = this.deliverymethod.sharedProduct.find(c => parseInt(c.id, 10) === parseInt(id, 10))
      if (product) {
        return product.name
      }
      return ''
    },
    getExcludedProductName (id) {
      const product = this.deliverymethod.excludedProduct.find(c => parseInt(c.id, 10) === parseInt(id, 10))
      if (product) {
        return product.name
      }
      return ''
    },
    selectIglooLock() {
      this.showModal('igloolockselector', IglooLockSelector, {
        props: {
          settings: this.settings,
          locale: this.shortLocale
        },
        events: {
          selectLock: this.iglooLockSelected
        },
        handlers: {},
        width: 800
      })
    },
    detachIglooLock() {
      this.deliverymethod.igloohome_lockcode = null
      this.deliverymethod.igloohome_lockname = null
    },
    iglooLockSelected (lock) {
      this.deliverymethod.igloohome_lockcode = lock._id
      this.deliverymethod.igloohome_lockname = lock.name
    },
    selectRatnaApartment() {
      this.showDialog({
        title: this._('Select apartment'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Select'),
            class: 'success',
            icon: 'check',
            handler: this.ratnaApartmentSelected,
            requiredFields: ['id']
          }
        ],
        fields: [
          {
            title: this._('Apartment'),
            type: 'select',
            name: 'id',
            options: this.ratnaApartments.map(a => { return { value: a.id, name: a.name } })
          },
        ]
      })
    },
    detachRatnaApartment() {
      this.deliverymethod.ratna_lockcode = null
      this.deliverymethod.ratna_lockname = null
    },
    ratnaApartmentSelected (data) {
      if (!data.id) {
        return
      }
      const apartment = this.ratnaApartments.find(a => a.id === data.id)
      if (apartment) {
        this.deliverymethod.ratna_lockcode = apartment.id
        this.deliverymethod.ratna_lockname = apartment.name
      }
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'price',
        'vat',
        'cutoffprice',
        'internal',
        'default',
        'shipit_id',
        'shipit_pricing',
        'shipit_margin',
        'shipit_returnfreightdoc',
        'maxheight',
        'maxwidth',
        'maxdepth',
        'maxweight',
        'singleproduct',
        'timetype',
        'fixedtime',
        'afterorder',
        'weekdaysafterorder',
        'weekdays',
        'weekdaystime',
        'beforereservationstart',
        'afterreservationstart',
        'beforereservationend',
        'afterreservationend',
        'beforeparentreservationstart',
        'afterparentreservationstart',
        'beforeparentreservationend',
        'afterparentreservationend',
        'beforefirstreservation',
        'afterfirstreservation',
        'beforelastreservation',
        'afterlastreservation',
        'igloohome_lockcode',
        'igloohome_lockname',
        'ratna_lockcode',
        'ratna_lockname',
        'lockduration',
        'autoprint',
        'sharedCategory',
        'sharedProduct',
        'excludedProduct',
        'sharedTag'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.deliverymethod[e], a), {})
      data.ownNotification = this.notifications
      data.version = this.version
      return data
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/deliverymethods/' + this.deliverymethod.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deletion failed!'))
      }
      this.close()
    },
    saveShipitAddress (address) {
      return new Promise(async (resolve) => {
        let response
        let data = Object.assign({}, address)
        data.type = 'deliverymethod'
        try {
          if (!address) {
            await axios.delete(this.baseDomain + this.shopId + '/deliverymethods/' + this.deliverymethod.id + '/addresses.json?apikey=' + this.apiKey, data)
          } else {
            await axios.put(this.baseDomain + this.shopId + '/deliverymethods/' + this.deliverymethod.id + '/addresses.json?apikey=' + this.apiKey, data)
          }
        } catch(e) {
          resolve()
          return
        }
        resolve()
      })
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/deliverymethods/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/deliverymethods.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      try {
        await this.saveShipitAddress(this.shipitAddress)
      } catch(e) {
        // uh oh
      }
      if (response && response.data && response.data.id) {
        await this.loadDeliverymethod(response.data.id)
      }
      this.saving = false
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    async reloadOnVersionChange () {
      if (!this.deliverymethod || !this.deliverymethod.id) {
        return
      }
      await this.loadDeliverymethod(this.deliverymethod.id)
    },
    loadDeliverymethod (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/deliverymethods/' + id + '.json?details=true&shipit=true&apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        if (!response.data.sharedTag) {
          response.data.sharedTag = []
        }
        if (response.data.shipit_returnfreightdoc === null || response.data.shipit_returnfreightdoc === undefined || response.data.shipit_returnfreightdoc === '') {
          response.data.shipit_returnfreightdoc = '1'
        }
        this.deliverymethod = response.data
        const loaders = []
        loaders.push(this.loadTags())
        loaders.push(this.loadCategories())
        loaders.push(this.loadProducts())
        if (this.isShipitEnabled) {
          loaders.push(this.loadShipitmethods())
        }
        loaders.push(this.loadShipitAddress(id))
        loaders.push(this.loadNotifications('deliverymethod', this.deliverymethod.id))
        await Promise.allSettled(loaders)
        this.storeState()
        this.loading = false
        resolve(this.deliverymethod)
      })
    },
    loadShipitmethods () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/shipitmethods.json?apikey=' + this.apiKey)
        } catch (e) {
          this.showError(e)
          reject()
          return
        }
        this.shipitMethods = response.data
        resolve(this.deliverymethod)
      })
    },
    loadShipitAddress (id) {
      return new Promise(async (resolve) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/deliverymethods/' + id + '/addresses.json')
        } catch(e) {
          this.shipitAddress = null
          resolve(null)
          return
        }
        this.shipitAddress = response.data
        resolve(response.data)
      })
    },
    loadTags () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/tags.json?type=delivery&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          this.showError(e)
          reject()
          return
        }
        this.allTags = response.data
        resolve(this.allTags)
      })
    },
    loadCategories () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/categories.json?simple=true&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          resolve([])
          return
        }
        this.allCategories = response.data
        resolve(this.allCategories)
      })
    },
    loadProducts () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/products.json?simple=true&sSearch_10=both&apikey=' + this.apiKey + this.versionStr)
        } catch(e) {
          resolve([])
          return
        }
        this.allProducts = response.data
        resolve(this.allProducts)
      })
    },
    updateTag (tagId) {
      if (!this.deliverymethod.sharedTag) {
        return
      }
      const newTag = this.allTags.find(tag => tag.id === tagId)
      const tagIndex = this.deliverymethod.sharedTag.findIndex(tag => tag.id === tagId)
      if (tagIndex !== -1) {
        this.deliverymethod.sharedTag[tagIndex].title = newTag.value
      }
    }
  },
  watch: {
    async tagToAdd(tag) {
      if (!tag || Object.entries(tag).length === 0 || !this.deliverymethod) {
        return
      }
      if (typeof tag === 'object' && tag.id !== 0) {
        this.deliverymethod.sharedTag.push({
          id: tag.id,
          title: tag.value
        })
      } else {
        if (!this.tags.find(t => t.title === 'delivery:::' + this.tagSearchText)) {
          this.deliverymethod.sharedTag.push({
            title: 'delivery:::' + this.tagSearchText
          })
        } 
      }
      await nextTick()
      this.tagToAdd = {}
    }
  },
  components: {
    DatetimePicker,
    DayHourMinuteSelector,
    DraggableMultiSelector,
    ModelListSelect,
    TagsList
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadDeliverymethod(this.component.initData.deliverymethodId)
    }
  },
  mounted () {
    if(!this.events) {
      let events = {}
      events['deliverymethodUpdate'] = async (message) => {
        if (message && message.data && this.deliverymethod && parseInt(message.data.id, 10) === parseInt(this.deliverymethod.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadDeliverymethod(message.data.id)
          }
        }
      }
      events['tagUpdate'] = async (message) => {
        if (message && message.data && this.deliverymethod) {
          await this.loadTags()
          this.updateTag(message.data.id)
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.deliverymethod && this.deliverymethod.id && parseInt(this.deliverymethod.id, 10) !== parseInt(this.component.initData.deliverymethodId, 10)) {
        try {
          await this.loadDeliverymethod(this.component.initData.deliverymethodId)
        } catch(e) {}
      }
    }
  }
}
</script>

<style scoped>
.daybuttons input {
  display: none;
}
</style>